/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import type { ProjectState } from '@/modules/shared/types/project.type';
import { isVideoMetadataGenresField } from '@/modules/shared/types/typeGuard';
import type { ActionContext } from 'vuex';
import _ from 'lodash';
import {
  deleteVideoCaption, uploadNewVideoCaption,
} from '@/modules/videoCaption/services';
import i18n from '@/plugins/lang/i18n';
import type { UploadNewCaptionPayload } from 'src/modules/videoCaption';
import {
  getVideoProcessingJobs,
  deleteCustomImageEntryById,
  deleteVideoByKey,
  editVideos,
  loadVideoByKey,
  loadVideoRevisions,
} from '@/modules/videoDetail/services';

import {
  isVideoProject,
  isVideoProbeErrorFile,
  isVideoProbeError,
  isVideoProbeNotComplete,
} from '@/modules/videoDetail/utils/videoManagement';
import {
  setStateSavingStatusByType,
  setStateLoadingStatusByType,
} from '@/modules/shared/utils/stateManagement';

import { formatErrorObject } from '@/modules/shared/utils/errorFormatter';
import type { RootState } from '@/store/type';
import type {
  UpdateVideoPayload,
  Video, VideoCaption, VideoState, VideoTranscode, Vod,
} from '@/modules/shared/types/video.type';
import type { VideoCustomImageField } from '@/modules/customImageField';
import type { VideoCustomField } from '@/modules/customField';
import type { LoadingState, SavingState } from '@/modules/shared/types/state.type';

type VideoActionContext = ActionContext<VideoState, RootState & { project?: ProjectState }>;

const { t } = i18n.global;

const getErrorMessageFromErrorStatus = (errorStatus: number) => {
  if (errorStatus === 500) {
    return { title: `${t('response:error500.title')} code : ${errorStatus}` };
  }
  if (errorStatus === 422) {
    return { title: t('response:error422.title') };
  }
  if (errorStatus === 404) {
    return { title: t('response:error404.title') };
  }
  if (errorStatus === 403) {
    return { title: t('response:error404.title') };
  }

  return { title: t('response:errorUnknown.title') };
};

export const getVideoDetailInitialState = (): VideoState => ({
  loadCurrentVideoState: setStateLoadingStatusByType(),
  saveCurrentVideoState: setStateSavingStatusByType(),
  probeCurrentVideoError: {},
  currentVideo: null,
  transcodeResult: null,
  refreshTimer: null,
  refreshTimerSecond: 50000,
  showDeleteVideoModal: false,
  isUploadingNewVideoCaption: false,
  customFields: [],
  customImages: {},
  videoMetadata: [],
  rating: '',
  publishedDate: '',
  selectedRevision: null,
  videoRevisions: [],
  loadVideoRevisionsState: setStateLoadingStatusByType(),
});

const state = getVideoDetailInitialState();

const getters = {
  currentVideo: (state: VideoState) => state.currentVideo,
  videoUploadData: (state: VideoState, rootGetters: any) => {
    const video = rootGetters.getVideoFromVideosUpload(state.currentVideo);
    if (isVideoProbeNotComplete(video)) {
      return video;
    }
    return state.currentVideo;
  },
  videoRevisions: (state: VideoState) => state.videoRevisions,
  loadVideoRevisionsState: (state: VideoState) => state.loadVideoRevisionsState,
};

const mutations = {
  setVideoRevisions(state: VideoState, videoRevisions: Vod[]) {
    state.videoRevisions = videoRevisions;
  },
  setLoadVideoRevisionsState(state: VideoState, loadingState: LoadingState) {
    state.loadVideoRevisionsState = setStateLoadingStatusByType(loadingState);
  },
  setCurrentVideo(state: VideoState, currentVideo: Video) {
    state.currentVideo = { ...state.currentVideo, ...currentVideo };
    if (state.saveCurrentVideoState.error) {
      if (!state.currentVideo.title) {
        state.saveCurrentVideoState.error = {
          ...state.saveCurrentVideoState.error,
          statusCode: 401,
          title: 'กรุณาระบุชื่อวิดีโอ',
        };
      } else {
        state.saveCurrentVideoState.error = {
          ...state.saveCurrentVideoState.error,
          title: '',
        };
      }
    }
  },
  setCustomImages(state: VideoState, data: VideoCustomImageField) {
    state.customImages = data;
  },
  setCustomFields(state: VideoState, data: VideoCustomField[]) {
    state.customFields = data;
  },
  setTranscodeResult(state: VideoState, transcodeResult: VideoTranscode[]) {
    state.transcodeResult = transcodeResult;
  },
  destroyCurrentVideo(state: VideoState) {
    Object.assign(state, getVideoDetailInitialState());
  },
  setLoadCurrentVideoStateMessage(state: VideoState, loadingState: LoadingState) {
    state.loadCurrentVideoState = setStateLoadingStatusByType(loadingState);
  },
  setSaveCurrentVideoStateMessage(state: VideoState, savingState: SavingState) {
    state.saveCurrentVideoState = setStateSavingStatusByType(savingState);
  },
  setProbeCurrentVideoError(state: VideoState, message: { errorStatus: string, errorCode: number }) {
    switch (message.errorStatus) {
      case 'probeError':
        state.probeCurrentVideoError.statusCode = message.errorCode;
        state.probeCurrentVideoError.iconClassName = 'fas fa-circle-exclamation';
        state.probeCurrentVideoError.title = t('video.upload.checkingFileError.title');
        state.probeCurrentVideoError.description = `${t('response:error404.helpText')}
          <a href="mailto:support@byteark.com" target="_top">support@byteark.com</a>`;
        break;
      case 'probeFileError':
        state.probeCurrentVideoError.statusCode = message.errorCode;
        state.probeCurrentVideoError.iconClassName = 'fas fa-triangle-exclamation';
        state.probeCurrentVideoError.title = t('video.upload.uploadedFileError.title');
        state.probeCurrentVideoError.description = t('video.upload.uploadedFileError.helpText');
        break;
      default:
        state.probeCurrentVideoError.statusCode = message.errorCode;
        state.probeCurrentVideoError.iconClassName = 'fas fa-circle-exclamation';
        state.probeCurrentVideoError.title = t('response:loadingFailed.title');
        state.probeCurrentVideoError.description = `${t('response:error404.helpText')}
          <a href="mailto:support@byteark.com" target="_top">support@byteark.com</a>`;
        state.probeCurrentVideoError.buttonTitle = t('response:backToHomepage');
        state.probeCurrentVideoError.buttonUrl = '/';
        break;
    }
  },
  toggleShowDeleteVideoModal(state: VideoState) {
    state.showDeleteVideoModal = !state.showDeleteVideoModal;
  },
  startUploadingVideoCaption(state: VideoState) {
    state.isUploadingNewVideoCaption = true;
  },
  finishUploadingVideoCaption(state: VideoState, { newCaption }: { newCaption: VideoCaption}) {
    state.isUploadingNewVideoCaption = false;
    if (newCaption && state.currentVideo) {
      state.currentVideo.vod.captions.push(newCaption);
    }
  },
  removeCaptionByKey(state: VideoState, { captionKey }: { captionKey: string}) {
    if (state.currentVideo) {
      state.currentVideo.vod.captions = state.currentVideo.vod.captions.filter(
        (caption) => caption.key !== captionKey,
      );
    }
  },
  setVideoMetadata(state: VideoState, value: VideoState['videoMetadata']) {
    state.videoMetadata = value;
  },
  setVideoRating(state: VideoState, value: VideoState['rating']) {
    state.rating = value;
  },
  setVideoPublishedDate(state: VideoState, value: VideoState['publishedDate']) {
    state.publishedDate = value;
  },
  setSelectedRevision(state: VideoState, value: VideoState['selectedRevision']) {
    state.selectedRevision = value;
  },
};

const actions = {
  async loadVideoRevisions({ commit }: VideoActionContext, videoKey: string) {
    if (!videoKey) {
      return;
    }

    commit('setLoadVideoRevisionsState', { type: 'loading' });
    try {
      const response = await loadVideoRevisions(videoKey);
      commit('setVideoRevisions', response.data);
      commit('setLoadVideoRevisionsState', { type: 'success' });
    } catch (error) {
      console.error(error);
      commit('setLoadVideoRevisionsState', {
        type: 'error',
        error: formatErrorObject(
          error,
          'Revisions',
          {
            404: t('video.revisions.table.empty'),
          },
        ),
      });
    }
  },
  async loadVideo({ commit, dispatch, rootState }: VideoActionContext, { videoKey, query = {}, silent = false }: { videoKey: string, query: { q?: string}, silent?: boolean }) {
    if (!silent) {
      commit('destroyCurrentVideo');
      commit('setLoadCurrentVideoStateMessage', { type: 'loading' });
    }
    try {
      const response = await loadVideoByKey(videoKey, query);
      const { data } = response;
      // Check if it's video of currentProject
      if (data && isVideoProject(data, rootState.project?.currentProject)) {
        commit('setCurrentVideo', data);
        commit('setCustomImages', data.simpleCustomImageFields);
        const probeErrorType = _.get(data, 'vod.source.probeErrorType', null);
        // set probeError message
        if (isVideoProbeError(data)) {
          const isProbeFileError = isVideoProbeErrorFile(data);
          if (isProbeFileError) {
            commit('setProbeCurrentVideoError', { errorStatus: 'probeFileError', errorCode: probeErrorType });
          } else {
            commit('setProbeCurrentVideoError', { errorStatus: 'probeError', errorCode: probeErrorType });
          }
        }
        await dispatch('loadTranscodeResult', videoKey);

        if (data.additionalVideoMetaData?.fields) {
          commit('setVideoRating', data.additionalVideoMetaData.fields.find((item) => item.fieldName === 'rating')?.value || null);

          const publishedDate = data.additionalVideoMetaData.fields.find((item) => item.fieldName === 'publishedDate')?.value;
          commit('setVideoPublishedDate', publishedDate ? `${publishedDate}T00:00:00.000Z` : null);
        }

        commit('setLoadCurrentVideoStateMessage', { type: 'success' });
      } else {
        commit('setLoadCurrentVideoStateMessage', {
          type: 'error',
          error: { statusCode: 404, message: t('response:error404.title', { item: 'Video' }) },
        });
      }
    } catch (error) {
      commit('setLoadCurrentVideoStateMessage', { type: 'error', error: formatErrorObject(error, 'video') });
    }
  },
  async loadTranscodeResult({ commit }: VideoActionContext, videoKey: string) {
    const response = await getVideoProcessingJobs(videoKey);
    commit('setTranscodeResult', response.data);
  },
  async updateVideo({ commit, state }: VideoActionContext, { videoKey, currentVideo, simpleCustomFields = null }: { videoKey: string, currentVideo: Video, simpleCustomFields: VideoCustomField[] | null}) {
    commit('setSaveCurrentVideoStateMessage', { type: 'saving' });

    /** Exclude customFields and customFieldEntries from a payload */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { customFields, customFieldEntries, ...rest }: Video = currentVideo;

    let payload: UpdateVideoPayload = rest;

    /** Update Lighthouse Metadata */
    const genres = state.videoMetadata.find((md) => md.fieldName === 'genres');

    payload = {
      ...payload,
      additionalVideoMetaData: {
        fields: [
          ...state.videoMetadata.filter((md) => md.fieldName !== 'genres').map((md) => ({
            displayName: md.displayName,
            fieldName: md.fieldName,
            value: md.value,
          })),
          {
            displayName: 'Rating',
            fieldName: 'rating',
            value: state.rating,
          },
          {
            displayName: 'Published Date',
            fieldName: 'publishedDate',
            value: state.publishedDate?.split('T')[0] ?? null,
          },
        ],
        genres: isVideoMetadataGenresField(genres) ? genres.value : [],
      },
    };

    /** Update Video's Custom Fields */
    if (simpleCustomFields && !_.isEmpty(simpleCustomFields)) {
      payload = {
        ...payload,
        simpleCustomFields,
      };
    }

    try {
      const updateResponse = await editVideos(videoKey, payload);
      commit('setCurrentVideo', updateResponse.data);

      setTimeout(() => {
        commit('setSaveCurrentVideoStateMessage', { type: 'idle' });
      }, 2000);
      commit('setSaveCurrentVideoStateMessage', { type: 'success' });
    } catch (error) {
      commit('setSaveCurrentVideoStateMessage', {
        type: 'error',
        error: formatErrorObject(error, 'Video'),
      });
    }
  },
  async deleteVideo({ commit }: VideoActionContext, videoKey: string) {
    try {
      commit('setSaveCurrentVideoStateMessage', { type: 'saving' });
      await deleteVideoByKey(videoKey);
      commit('setSaveCurrentVideoStateMessage', { type: 'success' });
      commit('destroyCurrentVideo');
    } catch (error: any) {
      const status = _.get(error, 'response.status', null);
      commit('setSaveCurrentVideoStateMessage', { type: 'error', error: { status, ...error.response.data, ...getErrorMessageFromErrorStatus(status) } });
    }
  },
  destroyCurrentVideo({ commit }: VideoActionContext) {
    commit('destroyCurrentVideo');
  },
  setCustomFields({ commit }: VideoActionContext, data: VideoCustomField[]) {
    commit('setCustomFields', data);
  },
  setCustomImages({ commit }: VideoActionContext, data: VideoCustomImageField) {
    commit('setCustomImages', data);
  },
  setCurrentVideo({ commit }: VideoActionContext, currentVideo: Video) {
    commit('setCurrentVideo', currentVideo);
  },
  toggleShowDeleteVideoModal({ commit }: VideoActionContext) {
    commit('toggleShowDeleteVideoModal');
  },
  async uploadNewCaptionForCurrentVideo({ commit, state }: VideoActionContext, { languageCode, file }: Omit<UploadNewCaptionPayload, 'customName'>) {
    const { currentVideo } = state;
    const requestPayload: UploadNewCaptionPayload = {
      languageCode,
      file,
      customName: languageCode === 'th' ? 'Thai (ไทย)' : 'English',
    };
    commit('startUploadingVideoCaption');
    try {
      if (currentVideo) {
        const response = await uploadNewVideoCaption(
          currentVideo.key,
          currentVideo.vod.revisionKey,
          requestPayload,
        );
        commit('finishUploadingVideoCaption', { newCaption: response.data });
      }
    } catch (err) {
      commit('finishUploadingVideoCaption');
    }
  },
  async deleteCaptionForCurrentVideo({ commit, state }: VideoActionContext, { captionKey }: { captionKey: string}) {
    const { currentVideo } = state;
    if (currentVideo) {
      await deleteVideoCaption(
        currentVideo.key,
        currentVideo.vod.revisionKey,
        captionKey,
      );
      commit('removeCaptionByKey', { captionKey });
    }
  },
  async deleteVideoCustomImageById({ state, commit }: VideoActionContext, { videoKey, customImageFieldId, customImageFieldName }: { videoKey: string, customImageFieldId: string, customImageFieldName: string}) {
    await deleteCustomImageEntryById(videoKey, customImageFieldId);
    const customImages: any = _.cloneDeep(state.customImages);
    customImages[customImageFieldName] = undefined;
    commit('setCustomImages', customImages);
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
